import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { layout } from "@literati/public-ui-shared-react";

const tabularNums = css`
  font-variant-numeric: tabular-nums;
`;

/** Corner inset for the orange stat icon (32×32); keep in sync with Card horizontal padding. */
const CARD_ICON_GUTTER = "0.75rem";

/**
 * Band behind the cards must differ from the card fill or the cards disappear visually:
 * `papier-blanc` / #fbfaf9 is the same as the old section bg (see theme `papier-blanc`).
 * Lit-5 reference: section #f6f6f5, cards #fbfaf9.
 */
const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3.5rem 0 4.5rem;
  box-sizing: border-box;
  background: rgb(246, 246, 245);
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
`;

const Inner = styled.div`
  ${layout.contentSpacing}
  box-sizing: border-box;
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 0;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }
`;

const HeadingCol = styled.div`
  max-width: 331px;
  width: 100%;
  padding: 1.5rem 0;
  display: flex;
  align-content: center;

  @media (max-width: 900px) {
    max-width: 100%;
    padding: 0;
    justify-content: center;
  }
`;

const HeadingText = styled.h2`
  font-family: ${({ theme }) => theme.font.fontFamily.hoeflerTitling};
  text-align: left;
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-size: 2.25rem;
  font-style: normal;
  font-weight: ${({ theme }) => theme.font.weight.roman};
  line-height: 125%;
  letter-spacing: -0.0225rem;
  margin: 0;

  @media (max-width: 900px) {
    text-align: center;
    font-size: 2rem;
  }
`;

const CardsCol = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  max-width: 766px;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    width: 100%;
  }
`;

const cardRevealVisible = css`
  opacity: 1;
  transform: translateY(0);
`;

/**
 * Opaque shell (background always full opacity). Entrance fade/slide lives on {@link CardReveal}
 * so `opacity` does not make the card background invisible before/during animation.
 */
const Card = styled.div`
  position: relative;
  flex: 1;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors["papier-blanc"]};
  display: flex;
  padding: 1.5rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 900px) {
    max-width: 350px;
    width: 100%;
    flex-direction: row;
    align-items: center;
    /* Symmetric L/R; icon is absolutely positioned on Card, not extra right padding hack */
    padding: 0.75rem 1rem;
    gap: 0.5rem;
    text-align: left;
  }
`;

const CardReveal = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out,
    transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);

  ${({ $visible }) => $visible && cardRevealVisible}

  @media (max-width: 900px) {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: auto;
    flex: 1;
    /* Keep copy from running under the absolutely positioned corner icon */
    min-width: 0;
    padding-right: calc(32px + ${CARD_ICON_GUTTER} + 0.25rem);
  }
`;

/** Sits on {@link Card} so it stays the visual upper-right of the card on all breakpoints. */
const CardIcon = styled.div`
  position: absolute;
  z-index: 2;
  top: ${CARD_ICON_GUTTER};
  right: ${CARD_ICON_GUTTER};
  left: auto;
  bottom: auto;
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: rgba(241, 112, 0, 0.1);
  pointer-events: none;
`;

const CircleOuter = styled.div`
  width: 110.514px;
  height: 110.514px;
  border-radius: 50%;
  border: 6.907px solid #a3a2a0;
  box-shadow: 0 0 8.634px 0 rgba(163, 162, 160, 0.5);
  transform: rotate(-45deg);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 80px;
    height: 80px;
    margin-bottom: 0;
  }
`;

const CircleInner = styled.div`
  display: flex;
  width: 82.885px;
  height: 82.885px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(241, 112, 0, 0.1);
  box-shadow: 0 0 2px 0 #f17000 inset;
  transform: rotate(45deg);

  @media (max-width: 900px) {
    width: 60px;
    height: 60px;
  }
`;

const PercentText = styled.span`
  ${tabularNums}
  color: #111618;
  text-align: center;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.72675rem;
  font-weight: ${({ theme }) => theme.font.weight.heavy};
  line-height: 100%;
  letter-spacing: -1px;

  @media (max-width: 900px) {
    font-size: 1.25rem;
  }
`;

const CardDesc = styled.div`
  color: ${({ theme }) => theme.colors["ink-black"]};
  text-align: center;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 140%;
  margin-top: 1.125rem;

  @media (max-width: 900px) {
    text-align: left;
    margin-top: 0;
    padding: 1.3rem 0;
  }
`;

const Bold = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.heavy};
`;

const FooterContainer = styled.div`
  max-width: 920px;
  text-align: center;
  margin: 2rem auto 0 auto;

  @media (max-width: 900px) {
    margin-top: 1rem;
  }
`;

const FooterText = styled.p`
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.25rem;
  font-weight: ${({ theme }) => theme.font.weight.roman};
  line-height: 150%;
  margin: 0;

  ${Bold} {
    font-weight: ${({ theme }) => theme.font.weight.bold};
  }

  @media (max-width: 900px) {
    font-size: 1.125rem;
    max-width: 310px;
    margin: 0 auto;
  }
`;

const SvgIconOrange = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="19"
    viewBox="0 0 17 19"
    fill="none"
    aria-hidden
  >
    <path
      d="M10.6 13.2222V11.9259H12.2851L8.91477 8.58797L6.32218 11.1806L1.52588 6.35186L2.43329 5.44445L6.32218 9.33334L8.91477 6.74075L13.1925 11.0185V9.33334H14.4888V13.2222H10.6Z"
      fill="#F17000"
    />
  </svg>
);

function animateCounter(
  element: HTMLElement,
  targetValue: number,
  opts: { suffix?: string; duration?: number }
) {
  const duration = opts.duration ?? 1800;
  const startTime = performance.now();

  function update(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease = 1 - (1 - progress) ** 3;
    const current = Math.round(ease * targetValue);
    element.innerText =
      opts.suffix != null ? `${current}${opts.suffix}` : String(current);
    if (progress < 1) requestAnimationFrame(update);
  }

  requestAnimationFrame(update);
}

const STATS = [
  {
    target: 14,
    suffix: "%" as const,
    description: (
      <>
        <Bold>Reading for fun is at an all-time low</Bold> (dropped 45% → 14%
        from 1984 to 2023<sup>[1]</sup>).
      </>
    ),
  },
  {
    target: 40,
    suffix: "%" as const,
    description: (
      <>
        <Bold>40% of 4th graders </Bold> are below NAEP &quot;Basic&quot;
        <sup>[2]</sup>.
      </>
    ),
  },
  {
    target: 34,
    suffix: "%" as const,
    description: (
      <>
        <Bold>34% of kindergarteners</Bold> start without basic skills
        <sup>[3]</sup>.
      </>
    ),
  },
];

/**
 * Post-hero literacy stats (funnel experiment lit-5), now first-class on the kids landing page.
 */
function LandingLiteracyStatsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState([false, false, false]);
  const startedRef = useRef(false);

  const runAnimations = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    STATS.forEach((stat, index) => {
      const el = document.getElementById(
        `landing-literacy-stats-counter-${index}`
      );
      if (!el) return;
      window.setTimeout(() => {
        setVisible((v) => {
          const next = [...v];
          next[index] = true;
          return next;
        });
        animateCounter(el, stat.target, { suffix: stat.suffix });
      }, index * 250);
    });
  }, []);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimations();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [runAnimations]);

  return (
    <Section ref={sectionRef} aria-labelledby="landing-literacy-stats-heading">
      <Inner>
        <TopRow>
          <HeadingCol>
            <HeadingText id="landing-literacy-stats-heading">
              Today&apos;s kids read less for fun—and it makes learning to read
              harder.
            </HeadingText>
          </HeadingCol>
          <CardsCol>
            {STATS.map((stat, index) => (
              <Card key={stat.target}>
                <CardIcon aria-hidden>
                  <SvgIconOrange />
                </CardIcon>
                <CardReveal $visible={visible[index]}>
                  <CircleOuter>
                    <CircleInner>
                      <PercentText
                        className="animate-number"
                        id={`landing-literacy-stats-counter-${index}`}
                      >
                        0{stat.suffix}
                      </PercentText>
                    </CircleInner>
                  </CircleOuter>
                  <CardDesc>{stat.description}</CardDesc>
                </CardReveal>
              </Card>
            ))}
          </CardsCol>
        </TopRow>
        <FooterContainer>
          <FooterText>
            You don&apos;t have to fix school. You are the best reading guide
            your child has.
            <Bold> And we&apos;re here to help.</Bold>
          </FooterText>
        </FooterContainer>
      </Inner>
    </Section>
  );
}

export default LandingLiteracyStatsSection;
